import {
  ChartTooltip,
  LineChart,
  getFilteredChartTooltipPayload
} from '@mantine/charts'
import { Card, Group, Skeleton, Stack, Text } from '@mantine/core'
import { ReactNode, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { GetDeviceMetricsParams } from '@/api/deviceApi'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { useGetDeviceMetrics } from '@/queries/deviceQueries'
import { buildMetricGrid } from './buildMetricGrid'
import { latestMetricValue } from './latestMetricValue'

type DeviceMetricChartProps = {
  deviceId: string
  metricKey: string
  referenceKey?: string
  referenceLabel?: string
  title: ReactNode
  badge?: ReactNode
  footer?: ReactNode
  valueFormatter?: (value: number) => string
  tickFormatter?: (ts: number) => string
  startTs: number
  endTs: number
  agg?: GetDeviceMetricsParams['agg']
  interval?: number
}

export const DeviceMetricChart = ({
  deviceId,
  metricKey,
  referenceKey,
  referenceLabel,
  title,
  badge,
  footer,
  valueFormatter,
  tickFormatter,
  startTs,
  endTs,
  agg,
  interval
}: DeviceMetricChartProps) => {
  const { data, isLoading, isError } = useGetDeviceMetrics(deviceId, {
    keys: referenceKey ? `${metricKey},${referenceKey}` : metricKey,
    startTs,
    endTs,
    agg,
    interval
  })

  const chartData = useMemo(() => {
    const rawPoints = (data?.[metricKey] ?? [])
      .slice()
      .sort((a, b) => a.ts - b.ts) // API returns points newest first, sort ascending for the chart
      .map((point) => ({ ts: point.ts, value: parseFloat(point.value) }))

    return interval && rawPoints.length >= 2
      ? buildMetricGrid(rawPoints, interval, startTs, endTs)
      : rawPoints
  }, [data, metricKey, interval, startTs, endTs])

  const referenceValue = useMemo(
    () => (referenceKey ? latestMetricValue(data?.[referenceKey]) : null),
    [data, referenceKey]
  )

  if (isLoading) {
    return <Skeleton height={200} />
  }

  if (isError) {
    return (
      <PlatformAlert variant="error">
        <FormattedMessage id="deployments.details.observability.error" />
      </PlatformAlert>
    )
  }

  const referenceLines =
    referenceValue === null
      ? undefined
      : [
          {
            y: referenceValue,
            color: 'red.6',
            label: [
              referenceLabel,
              valueFormatter
                ? valueFormatter(referenceValue)
                : String(referenceValue)
            ]
              .filter(Boolean)
              .join(' '),
            labelPosition: 'insideTopLeft' as const,
            ifOverflow: 'extendDomain' as const
          }
        ]

  return (
    <Card padding="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={600}>
            {title}
          </Text>
          {badge}
        </Group>

        {chartData.length === 0 ? (
          <Text c="dimmed" size="sm">
            <FormattedMessage id="deployments.details.observability.noData" />
          </Text>
        ) : (
          <LineChart
            h={220}
            data={chartData}
            dataKey="ts"
            series={[{ name: 'value', color: 'blue.6' }]}
            referenceLines={referenceLines}
            valueFormatter={valueFormatter}
            tooltipProps={{
              content: ({ label, payload }) => (
                <ChartTooltip
                  label={
                    tickFormatter ? tickFormatter(Number(label)) : String(label)
                  }
                  payload={getFilteredChartTooltipPayload(payload ?? [])}
                  valueFormatter={valueFormatter}
                />
              )
            }}
            xAxisProps={{
              type: 'number',
              domain: [startTs, endTs],
              ticks: [startTs, Math.floor((startTs + endTs) / 2), endTs],
              tickFormatter
            }}
            lineChartProps={{ margin: { top: 10 } }}
            yAxisProps={{
              tickCount: 3,
              orientation: 'right',
              tickMargin: 20,
              // snap both ends to nearest 10 to keep ticks on round numbers
              domain: [
                (dataMin: number) => Math.floor(dataMin / 10) * 10,
                (dataMax: number) => Math.ceil(dataMax / 10) * 10
              ]
            }}
            connectNulls={false}
          />
        )}

        {footer}
      </Stack>
    </Card>
  )
}
